import { netForce } from './model';
import type { PlantParams } from './types';

/**
 * The physics engine owns the plant trajectory. Controllers never integrate the plant themselves.
 * `step` applies thrust T and disturbance d, held constant over one step (zero-order hold).
 */
export interface PhysicsEngine {
  readonly name: string;
  configure(plant: PlantParams, dt: number): void;
  setState(z: number, v: number): void;
  step(T: number, d: number): void;
  readonly z: number;
  readonly v: number;
  dispose(): void;
}

/**
 * Independent scalar reference integrator (classical RK4) used to verify the engine in tests.
 * It is never used to drive the student-facing animation.
 */
export class ScalarReferenceEngine implements PhysicsEngine {
  readonly name = 'scalar-rk4-reference';
  private plant!: PlantParams;
  private dt = 0.005;
  z = 0;
  v = 0;

  configure(plant: PlantParams, dt: number): void {
    this.plant = { ...plant };
    this.dt = dt;
  }

  setState(z: number, v: number): void {
    this.z = z;
    this.v = v;
  }

  step(T: number, d: number): void {
    const p = this.plant;
    const h = this.dt;
    const acc = (z: number, v: number) => netForce(p, z, v, T, d) / p.m;
    const k1z = this.v;
    const k1v = acc(this.z, this.v);
    const k2z = this.v + (h / 2) * k1v;
    const k2v = acc(this.z + (h / 2) * k1z, this.v + (h / 2) * k1v);
    const k3z = this.v + (h / 2) * k2v;
    const k3v = acc(this.z + (h / 2) * k2z, this.v + (h / 2) * k2v);
    const k4z = this.v + h * k3v;
    const k4v = acc(this.z + h * k3z, this.v + h * k3v);
    this.z += (h / 6) * (k1z + 2 * k2z + 2 * k3z + k4z);
    this.v += (h / 6) * (k1v + 2 * k2v + 2 * k3v + k4v);
  }

  dispose(): void {}
}
